/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { ArrowBack, ArrowForward } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import useT from '@/i18n/useT'
import type { ReviewPager } from '@/graphql'

type ReviewPagerControlsProps = {
  pager?: ReviewPager | null
  totalCount?: number
  startDate: Date | null
  endDate: Date | null
  displayName?: string | null
  loading?: boolean
  onStartDateChange: (value: Date | null) => void
  onEndDateChange: (value: Date | null) => void
  onApplyFilters: () => void
  onPrevious: () => void
  onNext: () => void
}

function ReviewPagerControls({
  pager = null,
  totalCount,
  startDate,
  endDate,
  displayName = null,
  loading = false,
  onStartDateChange,
  onEndDateChange,
  onApplyFilters,
  onPrevious,
  onNext,
}: ReviewPagerControlsProps) {
  const { t } = useT()
  const resolvedTotalCount = totalCount ?? pager?.totalCount ?? 0
  const currentIndex = pager ? pager.currentIndex : 0

  return (
    <Card>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack spacing={1}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={1}
            alignItems={{ lg: 'center' }}
            justifyContent="space-between"
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
            >
              {displayName ? (
                <Chip size="small" variant="outlined" label={displayName} />
              ) : null}
              <DatePicker
                label={t('review.filters.startDate', 'Start date')}
                value={startDate}
                maxDate={endDate ?? undefined}
                onChange={onStartDateChange}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: { minWidth: { lg: 148 }, maxWidth: { lg: 164 } },
                  },
                }}
              />
              <DatePicker
                label={t('review.filters.endDate', 'End date')}
                value={endDate}
                minDate={startDate ?? undefined}
                onChange={onEndDateChange}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: { minWidth: { lg: 148 }, maxWidth: { lg: 164 } },
                  },
                }}
              />
              <Button
                variant="outlined"
                onClick={onApplyFilters}
                disabled={loading}
                size="small"
                sx={{ minWidth: 108, height: 40 }}
              >
                {t('review.filters.apply', 'Apply filters')}
              </Button>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              flexShrink={0}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              justifyContent={{ lg: 'flex-end' }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={onPrevious}
                disabled={loading || !pager?.hasPrevious}
                size="small"
              >
                {t('review.pager.previous', 'Previous')}
              </Button>
              <Button
                variant="outlined"
                endIcon={<ArrowForward />}
                onClick={onNext}
                disabled={loading || !pager?.hasNext}
                size="small"
              >
                {t('review.pager.next', 'Next')}
              </Button>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  flexShrink: 0,
                  minWidth: 56,
                  textAlign: { xs: 'left', lg: 'right' },
                }}
              >
                {t('review.pager.position', {
                  current: currentIndex,
                  total: resolvedTotalCount,
                  defaultValue: `${currentIndex} of ${resolvedTotalCount}`,
                })}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

export default ReviewPagerControls
